/**
 * A formatação simples das mensagens — **negrito**, *itálico*, [link](url)
 * e quebras de linha — em nós que a tela desenha como elementos React.
 * Nada vira HTML cru: texto é sempre texto, e link só com endereço seguro.
 */
export type RichNode =
  | { type: 'text'; text: string }
  | { type: 'bold'; children: RichNode[] }
  | { type: 'italic'; children: RichNode[] }
  | { type: 'link'; href: string; text: string }
  | { type: 'break' };

const SAFE_HREF = /^(https?:\/\/|mailto:|tel:|\/(?!\/))/i;

export function isSafeHref(href: string): boolean {
  return SAFE_HREF.test(href.trim());
}

const PATTERNS: { re: RegExp; build: (m: RegExpExecArray) => RichNode }[] = [
  {
    re: /\[([^\]\n]+)\]\(([^)\s]+)\)/,
    build: (m) =>
      isSafeHref(m[2]!) ? { type: 'link', href: m[2]!, text: m[1]! } : { type: 'text', text: m[1]! },
  },
  { re: /\*\*([^\n]+?)\*\*/, build: (m) => ({ type: 'bold', children: parseInline(m[1]!) }) },
  { re: /\*([^\s*][^\n*]*?)\*/, build: (m) => ({ type: 'italic', children: parseInline(m[1]!) }) },
];

function parseInline(text: string): RichNode[] {
  const out: RichNode[] = [];
  let rest = text;
  while (rest) {
    // O padrão que aparece primeiro vence; empate fica com a ordem da lista.
    let best: { m: RegExpExecArray; build: (m: RegExpExecArray) => RichNode } | null = null;
    for (const p of PATTERNS) {
      const m = p.re.exec(rest);
      if (m && (!best || m.index < best.m.index)) best = { m, build: p.build };
    }
    if (!best) {
      out.push({ type: 'text', text: rest });
      break;
    }
    if (best.m.index > 0) out.push({ type: 'text', text: rest.slice(0, best.m.index) });
    out.push(best.build(best.m));
    rest = rest.slice(best.m.index + best.m[0].length);
  }
  return out;
}

export function parseRichText(text: string): RichNode[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  return lines.flatMap((line, i) =>
    i === 0 ? parseInline(line) : [{ type: 'break' } as const, ...parseInline(line)],
  );
}
